import React from 'react';
import { TransProps } from '@lingui/react';
import { getColor } from 'components/utils';
import { SelectorIcon } from '@heroicons/react/solid';
import { Loader } from "../Loader/Loader";

export type typeInputSearch = {
  id: string;
  name?: string;
  value?: string;
  variant?: string;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  onClear?: () => void;
  labelClassName?: string;
  inputClassName?: string;
  helper?: React.ReactNode;
  asteriskPlacement?: 'right';
  inputWrapClassName?: string;
  errorMessage?: React.ReactNode;
  autoComplete?: string | undefined;
  label?: React.ReactElement<TransProps>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyEnterDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const Autocomplete: React.FC<typeInputSearch> = (
  {
    id,
    name,
    label,
    value,
    helper,
    onBlur,
    variant,
    onFocus,
    onChange,
    disabled,
    placeholder,
    autoComplete,
    errorMessage,
    labelClassName,
    inputClassName,
    onKeyEnterDown,
    loading = false,
    required = false,
    asteriskPlacement,
    inputWrapClassName,
  }
) => {
  const color = getColor(variant)

  const labelStyles = [ `${ !!helper ? 'mb-6' : '' } block mb-1 text-sm font-medium text-gray-700`, labelClassName ].join(' ');

  const inputStyles = [
    `appearance-none block w-full px-3 py-2 border ${
      errorMessage ? "border-red-500" : "border-gray-300"
    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-${
      errorMessage ? "red" : color
    }-500 focus:border-${errorMessage ? "red" : color}-500 sm:text-sm`,
    inputClassName,
    disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "",
  ].join(" ");

  const onKeyEnterDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (onKeyEnterDown) onKeyEnterDown(e)
    }
  }

  return (
    <>
      {
        label ? (
          <label
            htmlFor={ id }
            className={ labelStyles }
          >
            { required && !asteriskPlacement &&
							<span className="text-red-600 mr-1">*</span> } { label } { required && asteriskPlacement === 'right' &&
						<span className="text-red-600 mr-1">*</span> }
          </label>
        ) : null
      }
      <div className={ `relative rounded-md ${ inputWrapClassName || '' }` }>
        <div className='relative'>
          <input
            id={ id }
            type="text"
            name={ name }
            value={ value }
            maxLength={ 150 }
            onBlur={ onBlur }
            onFocus={ onFocus }
            required={ required }
            onChange={ onChange }
            className={ inputStyles }
            placeholder={ placeholder }
            autoComplete={ autoComplete }
            onKeyDown={ (e) => onKeyEnterDownHandler(e) }
          />
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <SelectorIcon
              aria-hidden="true"
              className="h-5 w-5 text-gray-400"
            />
          </span>
          { loading && (
            <div className='absolute inset-y-0 right-3 my-auto z-20 h-5 w-5'>
              <Loader size={ 5 }/>
            </div>
          ) }
        </div>
        {errorMessage ? (
          <p className="text-xs mt-1 text-red-500">{errorMessage}</p>
        ) : null}
        { !!helper && <p style={ { color: '#6B7280' } } className='italic mb-0 mt-1 px-4 text-xs'>{ helper }</p> }
      </div>
    </>
  )
}
